import { Anchor, Box, Button, Flex, Group, Stack, Divider, Text, Tooltip } from '@mantine/core';
import { useCallback, useMemo, useState } from 'react';
import { IconCopy, IconCheck } from '@tabler/icons';
import { DiscoveryEvidenceHeader } from './DiscoveryEvidenceHeader';
import { DiscoveryCardAttributes } from './DiscoveryCardAttributes';
import { DiscoveryEvidenceBodyByKind } from './DiscoveryEvidenceBodyByKind';
import { getDiscoveryEvidenceBodyKind } from './discoveryEvidenceBodyKind';
import { DiscoveryEvidenceMetadata } from './DiscoveryEvidenceMetadata';
import { DiscoveryEvidenceActions } from './DiscoveryEvidenceActions';
import { SourceIcon } from '../../../utils/SourceIcon';
import { resolveEvidenceDisplayOrigin } from '../../../utils/evidence/evidenceDisplayOrigin';
import type { EnrichedEvidence, EvidenceActionsConfig } from './types';

function buildEvidenceShareUrl(evidence: EnrichedEvidence): string {
  const ideaId = evidence.ideaId
    ? typeof evidence.ideaId === 'string'
      ? evidence.ideaId
      : (evidence.ideaId as any)?._id
    : evidence.idea?._id;
  if (ideaId) {
    return `${window.location.origin}/idea/${ideaId}?evidenceId=${evidence._id}`;
  }
  return `${window.location.origin}/evidence/${evidence._id}`;
}

function SourceOriginLink({ evidence }: { evidence: EnrichedEvidence }) {
  const cxItem = evidence.cxItem as Record<string, unknown> | undefined;
  const opportunity = evidence.opportunity as Record<string, unknown> | undefined;
  const origin = resolveEvidenceDisplayOrigin(evidence as any) as string | undefined;
  const link = (cxItem?.link || opportunity?.link || (evidence as any).link) as string | undefined;
  const originId = (cxItem?.originId || opportunity?.originId || evidence.originId || evidence.itemKey) as string | undefined;

  if (!origin) return null;

  return (
    <Flex align="center" gap={8} mt="xs">
      <SourceIcon sourceName={origin} height={18} width={18} />
      <Text fz="sm" sx={{ textTransform: 'capitalize' }}>
        {origin}
      </Text>
      {link && originId && (
        <Anchor
          size="sm"
          component="a"
          target="_blank"
          rel="noopener noreferrer"
          href={link}
          ml="xs"
        >
          Go To {originId}
        </Anchor>
      )}
    </Flex>
  );
}

function ShareButton({ evidence }: { evidence: EnrichedEvidence }) {
  const [copied, setCopied] = useState(false);

  const handleShare = useCallback(() => {
    const url = buildEvidenceShareUrl(evidence);
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 4000);
  }, [evidence]);

  return (
    <Tooltip label="Copy link" withArrow>
      <Button
        variant="subtle"
        compact
        bg="#F0F0FA"
        color='black'
        leftIcon={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
        onClick={handleShare}
        sx={{ height: 32, flexShrink: 0, fontSize: 14, fontWeight: 500 }}
      >
        {copied ? 'Copied' : 'Share'}
      </Button>
    </Tooltip>
  );
}

export interface DiscoveryEvidenceDetailsProps {
  evidence: EnrichedEvidence;
  variant?: 'full' | 'compact';
  showMetadata?: boolean;
  /** Accordion: header in trigger only; panel shows attributes + body + metadata. */
  panelPreset?: 'default' | 'exploreAccordion';
  loadEvidence?: () => void;
  actionsConfig?: EvidenceActionsConfig | null;
}

/**
 * Discovery/Explore evidence panel — does not use legacy CardBody / evidence-details package.
 */
export function DiscoveryEvidenceDetails({
  evidence,
  variant = 'full',
  showMetadata = true,
  actionsConfig = null,
  panelPreset = 'default',
  loadEvidence,
}: DiscoveryEvidenceDetailsProps) {
  const explore = panelPreset === 'exploreAccordion';
  const bodyKind = useMemo(() => getDiscoveryEvidenceBodyKind(evidence), [evidence]);

  const suppressTitle = explore;
  const bodySpacing = explore ? 6 : 'md';

  const attributesAndBody = (
    <Stack spacing={bodySpacing} sx={{ minWidth: 0 }}>
      <Group align="flex-start" noWrap spacing={6}>
        <DiscoveryCardAttributes item={evidence} loadEvidence={loadEvidence} />
        {explore && <ShareButton evidence={evidence} />}
      </Group>
      <DiscoveryEvidenceBodyByKind
        kind={bodyKind}
        item={evidence}
        isLoadingMl={false}
        suppressTitle={suppressTitle}
      />
      {explore && <SourceOriginLink evidence={evidence} />}
    </Stack>
  );

  return (
    <Stack spacing={explore ? 'xs' : 'sm'} sx={{ minWidth: 0 }}>
      {explore ? null : (
        <>
          <DiscoveryEvidenceHeader evidence={evidence} />
          {variant === 'full' && <Divider variant="dashed" my="xs" />}
        </>
      )}
      {variant === 'compact' ? (
        <Box mt={8} sx={{ maxHeight: 140, overflow: 'hidden' }}>
          {attributesAndBody}
        </Box>
      ) : (
        attributesAndBody
      )}
      {showMetadata && variant === 'full' && (
        <Box mt={explore ? 4 : 'sm'}>
          <DiscoveryEvidenceMetadata evidence={evidence} variant={variant} />
        </Box>
      )}
      {actionsConfig && (
        <DiscoveryEvidenceActions evidence={evidence} config={actionsConfig} />
      )}
    </Stack>
  );
}
